import React, {useCallback, useEffect, useState} from 'react';
import LoadingSpinner from '../../LoadingSpinner';
import {BACKEND_URL} from "../../utils/constants";
import getFirebaseAuthToken from "../../services/fireBaseAuthenticationService";
import useModalStore from "../../stores/modalStore";
import ReviewNotesModal from "../modals/ReviewNotesModal";
import ToDoListRow from "./ToDoListRow";
import useUserStore from "../../stores/userStore";
import {handleDelete, loggedAlert} from "../../services/eventService";
import { hasPermission } from "../../utils/permissions";

const RequestsTable = ( ) => {

    const { showReviewNotesModal, openModal, closeModal } = useModalStore();
    const { currentUser, requestList, setRequestList, setEmployeeEvents} = useUserStore();
    const canApprove = hasPermission('approve_deny_events');
    const [loading, setLoading] = useState(false);
    const [pendingDecision, setPendingDecision] = useState(null);
    const [submittingReview, setSubmittingReview] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkProcessing, setBulkProcessing] = useState(false);
    const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });

    const [timestampModes, setTimestampModes] = useState({
        insert: 'locale',
        event: 'locale',
        end: 'locale',
        reviewedAt: 'locale',
    });

    const toggleTimestampMode = (key) => {
        setTimestampModes(prev => ({
            ...prev,
            [key]: prev[key] === 'locale' ? 'epoch' : 'locale'
        }));
    };


    const handleDecision = (eventId, decisionType) => {
        setPendingDecision({ eventId, decisionType, bulk: false });
        openModal('reviewNotesModal');
    };

    const toggleSelect = (eventId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(eventId)) next.delete(eventId);
            else next.add(eventId);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === requestList.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(requestList.map(e => e.id)));
        }
    };

    const handleBulkDecision = (decisionType) => {
        if (selectedIds.size === 0) return;
        setPendingDecision({ eventId: null, decisionType, bulk: true });
        openModal('reviewNotesModal');
    };

    const submitReview = async (note) => {
        if (!pendingDecision) return;

        const { eventId, decisionType, bulk } = pendingDecision;
        setSubmittingReview(true);

        try {
            const token = await getFirebaseAuthToken();

            if (bulk) {
                const ids = Array.from(selectedIds);
                setBulkProcessing(true);
                setBulkProgress({ done: 0, total: ids.length });
                let failures = 0;

                for (let i = 0; i < ids.length; i++) {
                    try {
                        const result = await fetch(`${BACKEND_URL}/todolist/${ids[i]}/${decisionType}`, {
                            method: 'POST',
                            headers: {
                                Authorization: `Bearer ${token}`,
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({ note: note, user_id: currentUser.user_id }),
                        });
                        if (!result.ok) failures++;
                    } catch {
                        failures++;
                    }
                    setBulkProgress({ done: i + 1, total: ids.length });
                }

                if (failures > 0) {
                    loggedAlert(`${failures} of ${ids.length} events failed to ${decisionType}. Some may be in the streaming buffer.`, currentUser?.user_id);
                }
                setSelectedIds(new Set());
                setBulkProcessing(false);
                refreshRequestList(true);
            } else {
                const result = await fetch(`${BACKEND_URL}/todolist/${eventId}/${decisionType}`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ note: note, user_id: currentUser.user_id }),
                });
                if (!result.ok) {
                    loggedAlert(`Failed to ${decisionType} event: ${result.statusText}. Try again later. That event is probably in the 'streaming buffer' and should be ready for review in 15 minutes.`, currentUser?.user_id);
                } else {
                    refreshRequestList(true);
                }
            }

        } catch (err) {
            console.error(`Failed to ${decisionType} event(s):`, err);
        } finally {
            setSubmittingReview(false);
            closeModal('reviewNotesModal');
            setPendingDecision(null);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        const confirmed = window.confirm(`Delete ${selectedIds.size} selected event(s)?`);
        if (!confirmed) return;

        const ids = Array.from(selectedIds);
        setBulkProcessing(true);
        setBulkProgress({ done: 0, total: ids.length });

        const token = await getFirebaseAuthToken();
        let failures = 0;

        for (let i = 0; i < ids.length; i++) {
            try {
                const result = await fetch(`${BACKEND_URL}/timesheet/${ids[i]}?user_id=${currentUser.user_id}`, {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (!result.ok) failures++;
            } catch {
                failures++;
            }
            setBulkProgress({ done: i + 1, total: ids.length });
        }

        if (failures > 0) {
            loggedAlert(`${failures} of ${ids.length} events failed to delete.`, currentUser?.user_id);
        }

        setSelectedIds(new Set());
        setBulkProcessing(false);
        refreshRequestList(true);
    };


    const refreshRequestList = useCallback(async (showLoading = false) => {
        if (!currentUser?.authenticated) return;
        if (showLoading) setLoading(true);

        try {
            const token = await getFirebaseAuthToken();
            const url = canApprove
                ? `${BACKEND_URL}/todolist`
                : `${BACKEND_URL}/todolist?user_id=${currentUser.user_id}`;

            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const data = await res.json();

            if (Array.isArray(data)) {
                console.log('Received request list:', data);
                setRequestList(data);
            } else {
                console.error("Expected an array but received:", data);
                setRequestList([]);
            }
        } catch (err) {
            console.error('Failed to fetch to-do list:', err);
            setRequestList([]);
        } finally {
            setLoading(false);
        }
    }, [currentUser, setRequestList, setLoading, canApprove]);

    useEffect(() => {
        refreshRequestList(true); // Runs once on mount
    }, [refreshRequestList]);

    return (
        <div className="p-4 sm:p-6">
            {/* Bulk Action Bar */}
            {canApprove && selectedIds.size > 0 && (
                <div className="flex items-center gap-3 mb-4 p-3 bg-indigo-50 border border-indigo-100 rounded-xl">
                    <span className="text-sm font-semibold text-indigo-700">
                        {selectedIds.size} selected
                    </span>
                    {bulkProcessing ? (
                        <span className="text-xs text-gray-600 flex items-center gap-1.5">
                            <LoadingSpinner /> Processing {bulkProgress.done}/{bulkProgress.total}...
                        </span>
                    ) : (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handleBulkDecision('approve')}
                                className="!px-3 !py-1.5 !text-xs !font-semibold !bg-emerald-600 !text-white !border-none !rounded-lg hover:!bg-emerald-700 !transition-colors"
                            >
                                Approve All
                            </button>
                            <button
                                onClick={() => handleBulkDecision('deny')}
                                className="!px-3 !py-1.5 !text-xs !font-semibold !bg-rose-600 !text-white !border-none !rounded-lg hover:!bg-rose-700 !transition-colors"
                            >
                                Deny All
                            </button>
                            <button
                                onClick={handleBulkDelete}
                                className="!px-3 !py-1.5 !text-xs !font-semibold !bg-gray-600 !text-white !border-none !rounded-lg hover:!bg-gray-700 !transition-colors"
                            >
                                Delete All
                            </button>
                            <button
                                onClick={() => setSelectedIds(new Set())}
                                className="!px-3 !py-1.5 !text-xs !text-gray-500 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-50 !transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    )}
                </div>
            )}

            {loading ? (
                <div className="flex items-center justify-center py-16 text-gray-400 gap-2">
                    <LoadingSpinner /> Loading requests...
                </div>
            ) : requestList.length === 0 ? (
                <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
                    No pending requests
                </div>
            ) : (
                <div
                    className={`overflow-x-auto -mx-4 sm:-mx-6 ${
                        requestList.length > 5 ? 'max-h-[30rem] overflow-y-auto' : ''
                    }`}
                >
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-white z-10">
                        <tr className="border-b border-gray-100">
                            {canApprove && (
                                <th className="px-4 py-2.5 text-left w-8">
                                    <input
                                        type="checkbox"
                                        checked={requestList.length > 0 && selectedIds.size === requestList.length}
                                        onChange={toggleSelectAll}
                                        className="accent-indigo-600 cursor-pointer rounded"
                                        title="Select all"
                                    />
                                </th>
                            )}
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">User</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">Event</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Location</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide cursor-pointer" onClick={() => toggleTimestampMode('insert')}>
                                Inserted
                            </th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide cursor-pointer" onClick={() => toggleTimestampMode('event')}>
                                Event Time
                            </th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide cursor-pointer" onClick={() => toggleTimestampMode('end')}>
                                End Time
                            </th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide cursor-pointer" onClick={() => toggleTimestampMode('reviewedAt')}>
                                Reviewed
                            </th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">Notes</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">Value</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">Status</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">Reviewer</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">Review Notes</th>
                            {canApprove && (
                                <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-400 uppercase tracking-wide w-28">Actions</th>
                            )}
                        </tr>
                        </thead>
                        <tbody>
                        {requestList.map((event) => (
                            <ToDoListRow
                                key={event.id}
                                event={event}
                                onDecision={handleDecision}
                                onDelete={() => {
                                    setDeletingId(event.id);
                                    handleDelete(event, currentUser.user_id, setDeletingId, async () => {
                                        setEmployeeEvents(event.user_id, (prev) => prev.filter((e) => e.id !== event.id));
                                        setRequestList((prev) => prev.filter((e) => e.id !== event.id));
                                    });
                                }}
                                deleting={deletingId === event.id}
                                timestampModes={timestampModes}
                                selected={selectedIds.has(event.id)}
                                onToggleSelect={canApprove ? toggleSelect : undefined}
                            />
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            <ReviewNotesModal
                show={showReviewNotesModal}
                onClose={() => closeModal('reviewNotesModal')}
                onSubmit={submitReview}
                decisionType={pendingDecision?.decisionType}
                loading={submittingReview}
                bulkCount={pendingDecision?.bulk ? selectedIds.size : 0}
            />
        </div>
    );
};

export default RequestsTable;
